import { useState } from "react";
import { Building2, Check, ChevronDown, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useCompanyMemberships } from "@/hooks/useCompanyMemberships";
import { useCompany } from "@/hooks/useCompany";
import { toast } from "@/hooks/use-toast";

export function CompanySwitcher() {
  const { memberships, switchCompany, hasMultipleCompanies, loading: membershipsLoading } = useCompanyMemberships();
  const { company, refetch: refetchCompany } = useCompany();
  const [isSwitching, setIsSwitching] = useState(false);

  if (membershipsLoading || !company || memberships.length <= 1) {
    return null;
  }

  const handleSwitch = async (companyId: string) => {
    if (companyId === company.id || isSwitching) return;

    setIsSwitching(true);
    try {
      await switchCompany(companyId);
      await refetchCompany();
      toast({
        title: "Workspace switched",
        description: `Switched to ${memberships.find((m) => m.company_id === companyId)?.company?.name}`,
      });
      // Reload to reset all data contexts
      window.location.reload();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to switch workspace",
        variant: "destructive",
      });
    }
    setIsSwitching(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 max-w-[180px] text-left"
          disabled={isSwitching}
        >
          {isSwitching ? (
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          ) : (
            <Building2 className="w-4 h-4 shrink-0 text-primary" />
          )}
          <span className="truncate text-sm font-medium">{company.name}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <div className="px-2 py-1.5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Workspaces
          </p>
        </div>
        <DropdownMenuSeparator />
        {memberships.map((membership) => {
          const isActive = membership.company_id === company.id;
          return (
            <DropdownMenuItem
              key={membership.id}
              onClick={() => handleSwitch(membership.company_id)}
              className={cn(
                "flex items-center justify-between gap-2 cursor-pointer",
                isActive && "bg-primary/5"
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className={cn(
                  "w-7 h-7 rounded-md flex items-center justify-center shrink-0 text-xs font-bold",
                  isActive 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground"
                )}>
                  {membership.company?.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {membership.company?.name || "Unknown"}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {membership.role}
                  </p>
                </div>
              </div>
              {isActive && <Check className="w-4 h-4 text-primary shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
